/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFlask,
  faVirus,
  faLeaf,
  faThermometerHalf,
  faVolumeHigh,
  faUserShield,
  faFan,
  faRadiation,
  faBolt,
  faVial,
  faLightbulb,
  faUsers,
  faBuilding,
} from '@fortawesome/free-solid-svg-icons';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

interface ServiceCardProps {
  icon: any;
  title: string;
  delay?: number;
}

/** 🔹 Single card component */
const ServiceCard: React.FC<ServiceCardProps> = ({ icon, title, delay = 0 }) => {
  const controls = useAnimation();
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  React.useEffect(() => {
    if (isInView) controls.start('visible');
  }, [isInView, controls]);

  return (
    <div
      ref={ref}
      className="relative bg-[#081A26]/80 backdrop-blur-lg border border-[#00D4FF]/20 shadow-[0_0_25px_rgba(0,212,255,0.1)] rounded-2xl p-6 
      hover:border-[#00D4FF]/50 hover:shadow-[0_0_35px_rgba(0,212,255,0.3)] transition-all duration-300 group"
    >
      <div className="flex items-center gap-4">
        <div
          className="w-12 h-12 rounded-full bg-[#00D4FF]/10 flex justify-center items-center 
          group-hover:bg-[#00D4FF]/20 transition-all duration-300"
        >
          <FontAwesomeIcon
            icon={icon}
            className="text-[#00D4FF] text-xl group-hover:scale-110 transition-transform duration-300"
          />
        </div>
        <h3 className="font-semibold text-white group-hover:text-[#00D4FF] text-base leading-snug">
          {title}
        </h3>
      </div>
    </div>
  );
};

/** 🔹 Main section */
export const OurSolutionsSection: React.FC = () => {
  const services = [
    { title: 'Occupational Health Risk Assessment', icon: faUserShield },
    { title: 'Ergonomics Risk Assessment', icon: faUsers },
    { title: 'Chemical Risk Assessment', icon: faFlask },
    { title: 'Environmental Risk Assessment', icon: faLeaf },
    { title: 'Asbestos Survey', icon: faVial },
    { title: 'Illumination Survey', icon: faLightbulb },
    { title: 'General Ventilation and Indoor Air Quality Survey', icon: faFan },
    { title: 'Noise Survey', icon: faVolumeHigh },
    { title: 'Extraction Ventilation Survey', icon: faFan },
    { title: 'Hazardous Chemical Agents Survey', icon: faFlask },
    { title: 'Heat Stress Survey', icon: faThermometerHalf },
    { title: 'Cold Stress Survey', icon: faThermometerHalf },
    { title: 'Whole Body and Hand Arm Vibration Survey', icon: faBolt },
    { title: 'Ionizing Radiation Survey', icon: faRadiation },
    { title: 'Hazardous Biological Agents Survey', icon: faVirus },
    { title: 'Facilities Survey', icon: faBuilding },
  ];

  return (
    <section id="solutions" className="relative py-24 overflow-hidden text-white">
      {/* ✨ Smooth Refined Gradient Layers */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#01303E] via-[#022836] to-[#010E13]" />
      <div className="absolute bottom-0 left-0 w-full h-72 bg-gradient-to-b from-transparent via-[#021620]/90 to-[#03010E]" />

      <div className="relative container mx-auto px-6 md:px-12">
        {/* HEADER */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#00D4FF] tracking-wide">
            Occupational Hygiene Services
          </h2>
          <p className="mt-3 text-[#A0CFE9] max-w-2xl mx-auto text-lg">
            Comprehensive risk and exposure assessments for a safer, healthier workplace.
          </p>
        </div>

        {/* GRID */}
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 relative z-10">
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              icon={service.icon}
              title={service.title}
              delay={index * 0.05}
            />
          ))}
        </div>
      </div>

      {/* Subtle cyan ambient behind content */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,212,255,0.06)_0%,transparent_85%)] pointer-events-none" />
    </section>
  );
};

export default OurSolutionsSection;
